import { Injectable } from "@angular/core";
import _ from "lodash";

import { ItemDetail } from "@/model/entities/item-detail";
import { ItemDetailLog } from "@/model/entities/item-detail-log";
import { ItemDetailStatusEnum } from "@/model/entities/enums";
import { DbQueryService } from "./db-query.service";

/**
 * Item Story — plain-English narrative timeline for a single ItemDetail.
 *
 * Consumes ItemDetailLog rows + the ItemDetail's expanded navigation
 * properties and produces a chronological list of StoryEvent records.
 * Anomalies (regressions, orphaned invoice links, stale states) are flagged
 * with isAnomaly = true so the UI can highlight them.
 */

export type StoryEventKind =
  | "created"
  | "inventoryReceived"
  | "pulledForOrder"
  | "addonQueued"
  | "addonInProcess"
  | "addonComplete"
  | "shipPrep"
  | "shipHeld"
  | "voucherCreated"
  | "shipped"
  | "damaged"
  | "missing"
  | "regression"
  | "invoiceLinkOrphaned"
  | "statusUnknown";

export interface StoryEvent {
  timestamp: Date;
  actor: string;
  kind: StoryEventKind;
  headline: string;
  detail: string;
  isAnomaly: boolean;
  anomalyReason?: string;
  rawStatusId: ItemDetailStatusEnum;
}

@Injectable({ providedIn: "root" })
export class ItemStoryService {
  constructor(private dbQueryService: DbQueryService) {}

  /**
   * Build the ordered narrative for one item. Returns oldest event first.
   * Adds one synthetic "current state" event at the end summarising where the
   * item stands right now — including any invoice-link orphaned condition.
   */
  async buildStory(itemDetailId: string): Promise<StoryEvent[]> {
    const [itd, logsResult] = await Promise.all([
      this.dbQueryService.getItemDetailById(itemDetailId),
      this.dbQueryService.createItemDetailLogQuery(itemDetailId).execute(),
    ]);

    if (!itd) {
      return [];
    }

    // Sort chronologically, then collapse consecutive rows that share the
    // same ItemDetailStatusId + JoDetailId + PoDetailId. Those extra rows are
    // usually just BarcodePrintStatus changes (or plain re-saves) and should
    // not appear as separate story events — the item's operational state
    // didn't change. We keep the FIRST row of each run as the canonical event.
    const sortedLogs = (logsResult as unknown as ItemDetailLog[]).slice()
      .sort((a, b) => a.crtnTs.getTime() - b.crtnTs.getTime());

    const logs: ItemDetailLog[] = [];
    for (const log of sortedLogs) {
      const prev = _.last(logs);
      const isDupOfPrev =
        prev != null &&
        prev.itemDetailStatusId === log.itemDetailStatusId &&
        (prev.joDetailId ?? null) === (log.joDetailId ?? null) &&
        (prev.poDetailId ?? null) === (log.poDetailId ?? null);
      if (!isDupOfPrev) logs.push(log);
    }

    const events: StoryEvent[] = [];

    // Track the high-water mark so we can detect real regressions (going back
    // to Ship Prep after already reaching Voucher, etc.), but ignore the
    // legitimate addon-flow ordering where 20 (InProcessAddon) hands back to
    // 10 (InProcessPendingShipPrep).
    let highWaterStatus: ItemDetailStatusEnum | null = null;
    for (const log of logs) {
      const event = this.translateLogRow(log, itd, highWaterStatus);
      events.push(event);
      if (highWaterStatus == null || log.itemDetailStatusId > highWaterStatus) {
        highWaterStatus = log.itemDetailStatusId;
      }
    }

    const currentState = this.buildCurrentStateEvent(itd, logs);
    if (currentState) events.push(currentState);

    return events;
  }

  private translateLogRow(
    log: ItemDetailLog,
    itd: ItemDetail,
    highWaterStatus: ItemDetailStatusEnum | null,
  ): StoryEvent {
    const actor = log.crtnUserInit || "system";
    const isRegression = this.isTrueRegression(highWaterStatus, log.itemDetailStatusId);

    const base: Omit<StoryEvent, "kind" | "headline" | "detail" | "isAnomaly" | "anomalyReason"> = {
      timestamp: log.crtnTs,
      actor,
      rawStatusId: log.itemDetailStatusId,
    };

    const { kind, headline, detail } = this.describeStatus(log, itd);

    if (isRegression) {
      return {
        ...base,
        kind: "regression",
        headline: `\u{1F6A8} Status regressed: ${headline}`,
        detail,
        isAnomaly: true,
        anomalyReason: `Item had previously reached ${this.statusName(highWaterStatus!)} but was moved backward to ${this.statusName(log.itemDetailStatusId)}. This should not normally happen.`,
      };
    }

    return { ...base, kind, headline, detail, isAnomaly: false };
  }

  /**
   * A true regression is a move to a pre-shipping-prep status AFTER the item
   * has already reached Ship Prep (30) or beyond. This excludes the normal
   * addon flow, where an item legitimately transitions:
   *   InProcessPendingAddon (11) -> InProcessAddon (20) -> InProcessPendingShipPrep (10)
   * even though 10 < 20 numerically.
   */
  private isTrueRegression(
    highWaterStatus: ItemDetailStatusEnum | null,
    currentStatus: ItemDetailStatusEnum,
  ): boolean {
    if (highWaterStatus == null) return false;
    // Only flag when the item had already made it to Ship Prep or beyond
    // AND is now being knocked back below Ship Prep.
    return (
      highWaterStatus >= ItemDetailStatusEnum.InProcessShipPrep &&
      currentStatus < ItemDetailStatusEnum.InProcessShipPrep
    );
  }

  private describeStatus(log: ItemDetailLog, itd: ItemDetail): {
    kind: StoryEventKind;
    headline: string;
    detail: string;
  } {
    const jo = log.joDetail?.joHeader;
    const joLabel = jo ? `JO ${jo.id} (${jo.account?.accountName ?? "unknown account"})` : null;
    const bin = log.itemBin?.name ? `bin ${log.itemBin.name}` : null;
    const productLabel = this.productLabel(itd);

    switch (log.itemDetailStatusId) {
      case ItemDetailStatusEnum.InInventory:
        return {
          kind: "inventoryReceived",
          headline: bin ? `Received into inventory (${bin})` : "Received into inventory",
          detail: log.poDetailId
            ? `Recorded against PO ${log.poDetail?.poHeaderId ?? log.poDetailId}.`
            : "Available for future orders.",
        };

      case ItemDetailStatusEnum.InProcessPendingShipPrep:
        return {
          kind: "shipPrep",
          headline: joLabel ? `Pending Ship Prep for ${joLabel}` : "Pending Ship Prep",
          detail: bin
            ? `${productLabel} is ready for the packer to scan into a shipping bin.`
            : `${productLabel} is ready for shipping.`,
        };

      case ItemDetailStatusEnum.InProcessPendingAddon:
        return {
          kind: "addonQueued",
          headline: joLabel ? `Queued for addon (${joLabel})` : "Queued for addon work",
          detail: `${productLabel} needs decoration/embellishment before it can ship.`,
        };

      case ItemDetailStatusEnum.InProcessAddon:
        return {
          kind: "addonInProcess",
          headline: "Addon work in progress",
          detail: `${productLabel} is currently at an addon station.`,
        };

      case ItemDetailStatusEnum.InProcessShipPrep:
        return {
          kind: "shipPrep",
          headline: bin ? `In Ship Prep (${bin})` : "In Ship Prep",
          detail: joLabel
            ? `Scanned into shipping and waiting for voucher/label creation for ${joLabel}.`
            : "Scanned into shipping.",
        };

      case ItemDetailStatusEnum.InProcessShipHeld:
        return {
          kind: "shipHeld",
          headline: "Ship Held",
          detail: "Placed on hold in shipping — awaiting release before it can voucher.",
        };

      case ItemDetailStatusEnum.InProcessVoucher:
        return {
          kind: "voucherCreated",
          headline: itd.invoiceHeaderId
            ? `On voucher (invoice ${itd.invoiceHeaderId})`
            : "On voucher",
          detail: joLabel
            ? `Committed to an invoice for ${joLabel}. Physical shipment happens next.`
            : "Committed to an invoice.",
        };

      case ItemDetailStatusEnum.Shipped:
        return {
          kind: "shipped",
          headline: itd.invoiceHeaderId
            ? `Shipped (invoice ${itd.invoiceHeaderId})`
            : "Shipped",
          detail: joLabel
            ? `Invoice posted to QuickBooks and item marked as shipped for ${joLabel}.`
            : "Marked as shipped.",
        };

      case ItemDetailStatusEnum.Special:
        return {
          kind: "statusUnknown",
          headline: "Marked as Special",
          detail: "Set aside outside the normal shipping pipeline.",
        };

      case ItemDetailStatusEnum.MigratedUnknown:
        return {
          kind: "statusUnknown",
          headline: "Migrated (state unknown)",
          detail: "Carried over from the legacy system with no known status at migration time.",
        };

      case ItemDetailStatusEnum.MissingFromInventory:
        return {
          kind: "missing",
          headline: "Marked Missing from Inventory",
          detail: "Expected on the shelf but couldn't be located.",
        };

      case ItemDetailStatusEnum.DamagedOrOther:
        return {
          kind: "damaged",
          headline: "Marked Damaged / Other",
          detail: "Removed from the normal pipeline due to damage or an ad-hoc condition.",
        };

      default:
        return {
          kind: "statusUnknown",
          headline: `Status ${log.itemDetailStatusId} (${this.statusName(log.itemDetailStatusId)})`,
          detail: "Uncategorised status transition.",
        };
    }
  }

  /**
   * Emit a "here's where it stands right now" event, flagging anomalies like
   * an item that carries an invoice link while its status is sub-voucher
   * (the misalignment fingerprint from the addon-cleanup bug).
   */
  private buildCurrentStateEvent(itd: ItemDetail, logs: ItemDetailLog[]): StoryEvent | null {
    const lastLog = _.last(logs);
    const timestamp = lastLog?.crtnTs ?? new Date();

    const invoiceLinkOrphaned =
      itd.invoiceHeaderId != null && itd.itemDetailStatusId < ItemDetailStatusEnum.InProcessVoucher;

    if (invoiceLinkOrphaned) {
      return {
        timestamp,
        actor: "system",
        kind: "invoiceLinkOrphaned",
        headline: `\u{1F6A8} Currently misaligned: on invoice ${itd.invoiceHeaderId} but status is ${this.statusName(itd.itemDetailStatusId)}`,
        detail:
          "This item is bound to an invoice yet appears earlier in the pipeline. This is the voucher-status misalignment fingerprint. Ship-prep grids will show it; customer billing is unaffected.",
        isAnomaly: true,
        anomalyReason:
          "InvoiceHeaderId is set while ItemDetailStatusId < InProcessVoucher (45). The addon-cleanup regression bug (fixed 2026-08-20) produced this pattern historically.",
        rawStatusId: itd.itemDetailStatusId,
      };
    }

    return null;
  }

  private productLabel(itd: ItemDetail): string {
    const pt = itd.product?.productType;
    if (!pt) return "This item";
    const style = pt.style ? `${pt.style}` : "";
    const desc = pt.description ? ` — ${pt.description}` : "";
    return style ? `${style}${desc}` : (pt.description ?? "This item");
  }

  private statusName(id: ItemDetailStatusEnum): string {
    return ItemDetailStatusEnum[id] ?? `Unknown(${id})`;
  }
}
